export const TABLE_DISPLAY_PER_PAGE = 5;
export const CHART_DISPLAY_MAX_ITEMS = 10;

export enum ReportPreMadeTemplate {
  resultSummary = 'resultSummary',
  forms = 'forms',
  actions = 'actions',
  negativeResponses = 'negativeResponses',
  scores = 'scores',
  overdueForms = 'overdueForms',
}

export enum ReportFilterDateRange {
  today = 'today',
  yesterday = 'yesterday',
  lastWeek = 'last_week',
  lastSevenDays = 'last_seven_days',
  lastThirtyDays = 'last_thirty_days',
  lastMonth = 'last_month',
  custom = 'custom'
}

export enum WidgetDisplayOptions {
  singleNumber = 'singleNumberDisplay',
  barChart = 'barChartDisplay',
  lineChart = 'lineChartDisplay',
  pieChart = 'pieChartDisplay',
  table = 'tableDisplay',
}

export enum FilterType {
  date = 'date',
  folder = 'folder',
  entity = 'entity',
  group = 'group',
  user = 'user',
  category = 'category',
  checklist = 'checklist',
  punctuality = 'punctuality',
  section = 'section',
  formItem = 'formItem',
  priority = 'priority',
  formStatus = 'formStatus',
  actionStatus = 'actionStatus',
  creator = 'creator',
  response = 'response',
  approvalStatus = 'approvalStatus',
  approvers = 'approvers',
  approvalDate = 'approvalDate',
}

export const optionalFilters: Array<FilterType> = [FilterType.section, FilterType.formItem, FilterType.priority,
  FilterType.formStatus, FilterType.actionStatus, FilterType.creator, FilterType.response, FilterType.approvalStatus,
  FilterType.approvers, FilterType.approvalDate];

export enum PunctualityType {
  tooLate = 0,
  inTime = 1,
}

export enum PriorityType {
  low = 0,
  normal = 10,
  high = 20,
}
export enum FormStatusType {
  Due = 10,
  Overdue = 20,
}

export type LineChartTimeGroupingType = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type NullableLineChartTimeGroupingType = LineChartTimeGroupingType | null;

export const metrics = ['submitted-forms', 'form-score', 'form-items', 'negative-responses', 'form-item-score',
  'schedule-forms', 'actions'] as const;

export type MetricsType = (typeof metrics)[number];

export type DefaultAttrsType = 'users' | 'templates' | 'groups' | 'entities' | 'folders' | 'categories' | 'day' | 'week'
| 'month' | 'quarter' | 'year';

export type PunctualityAttribute = 'punctuality';
export type ActionStatusAttribute = 'action-status';
export type FormStatusAttribute = 'form-status';
export type TotalAttribute = 'total';
export type CreatorAttribute = 'creator';
export type PriorityAttribute = 'priority';
export type PageSectionAttribute = 'page-section';
export type FormItemAttribute = 'form-item';
export type AverageAttribute = 'average';
export type ResponseAttribute = 'response';
export type ApprovalStatusAttribute = 'approval-status';
export type ApproversAttribute = 'approvers';

export type OptionalAttrsType = PunctualityAttribute
| ActionStatusAttribute
| FormStatusAttribute
| TotalAttribute
| CreatorAttribute
| PriorityAttribute
| PageSectionAttribute
| FormItemAttribute
| AverageAttribute
| ResponseAttribute
| ApprovalStatusAttribute
| ApproversAttribute;

export type AllAttributesType = DefaultAttrsType | OptionalAttrsType;

// Widget dataset when these attributes are selected will contain only one value
export const singleValueAttributes: Array<AllAttributesType> = ['total', 'average'];
// Widget dataset when these attributes are selected will contain only absolute values
export const noPercentageValuesAttributes: Array<AllAttributesType> = ['total'];
// Widget dataset when these attributes are selected will contain only percentage values
export const onlyPercentageValuesAttributes: Array<AllAttributesType> = ['average'];

export const defaultAttrs = (): Array<DefaultAttrsType> => ['users', 'templates', 'groups', 'entities', 'folders',
  'categories', 'day', 'week', 'month', 'quarter', 'year'];

export type SubmittedFormsAttrsType = DefaultAttrsType | PunctualityAttribute | TotalAttribute
| ApprovalStatusAttribute | ApproversAttribute;
export const submittedFormsAttrs = (): Array<SubmittedFormsAttrsType> => [...defaultAttrs(), 'punctuality', 'total',
  'approval-status', 'approvers'];

export type FormScoreAttrsType = DefaultAttrsType | PunctualityAttribute | AverageAttribute | FormItemAttribute
| PageSectionAttribute | ApprovalStatusAttribute | ApproversAttribute;
export const formScoreAttrs = (): Array<FormScoreAttrsType> => [...defaultAttrs(), 'punctuality', 'average',
  'form-item', 'page-section', 'approval-status', 'approvers'];

export type FormItemsAttrsType = DefaultAttrsType | PunctualityAttribute | TotalAttribute | ResponseAttribute
| ApprovalStatusAttribute | ApproversAttribute;
export const formItemsAttrs = (): Array<FormItemsAttrsType> => [...defaultAttrs(), 'punctuality', 'total', 'response',
  'approval-status', 'approvers'];

export type NegativeResponsesAttrsType = DefaultAttrsType | PunctualityAttribute | TotalAttribute
| PageSectionAttribute | FormItemAttribute | ResponseAttribute | ApprovalStatusAttribute | ApproversAttribute;
export const negativeResponsesAttrs = (): Array<NegativeResponsesAttrsType> => [...defaultAttrs(), 'punctuality',
  'total', 'page-section', 'form-item', 'response', 'approval-status', 'approvers'];

export type FormItemScoreAttrsType = DefaultAttrsType | PunctualityAttribute | AverageAttribute | FormItemAttribute
| ApprovalStatusAttribute | ApproversAttribute;
export const formItemScoreAttrs = (): Array<FormItemScoreAttrsType> => [...defaultAttrs(), 'punctuality', 'average',
  'approval-status', 'approvers', 'form-item'];

export type ScheduleFormsAttrsType = DefaultAttrsType | FormStatusAttribute | TotalAttribute;
export const scheduleFormsAttrs = (): Array<ScheduleFormsAttrsType> => [...defaultAttrs(), 'form-status', 'total'];

export type ActionsAttrsType = DefaultAttrsType | PunctualityAttribute | TotalAttribute | ActionStatusAttribute
| CreatorAttribute | PriorityAttribute | FormItemAttribute | PageSectionAttribute| ApprovalStatusAttribute | ApproversAttribute;
export const actionsAttrs = (): Array<ActionsAttrsType> => [...defaultAttrs(), 'punctuality', 'total', 'action-status',
  'creator', 'priority', 'form-item', 'page-section', 'approval-status', 'approvers'];

export const allAttrs: Array<AllAttributesType> = [...defaultAttrs(), 'punctuality', 'action-status', 'form-status',
  'total', 'creator', 'priority', 'page-section', 'form-item', 'average', 'response', 'approval-status', 'approvers'];
export const allAttrsEnum: Record<AllAttributesType, AllAttributesType> = allAttrs.reduce((acc, value) => {
  acc[value] = value;
  return acc;
}, {} as Record<AllAttributesType, AllAttributesType>);

export enum ValueTypes {
  absolute = 'absolute',
  ratio = 'ratio',
}

const defaultLineChartTimeGroupings: Array<LineChartTimeGroupingType> = ['day', 'week', 'month', 'quarter', 'year'];

export type AnalyticsMap = Map<MetricsType, MetricMapItem>;

type MetricMapItem = {
  enabledValueTypes: Array<ValueTypes>;
  enabledAttributes: Map<AllAttributesType, AttributeMapItem>;
};

type AttributeMapItem = {
  enabledValueTypes: Array<ValueTypes>;
  enabledDisplays: Array<WidgetDisplayOptions>;
  enabledLineChartTimeGroupings: Array<LineChartTimeGroupingType>;
  defaultDisplay: WidgetDisplayOptions | null;
};

const getEnabledValueTypes = (attribute: AllAttributesType) => {
  switch (attribute) {
    case allAttrsEnum.total:
      return [ValueTypes.absolute];
    case allAttrsEnum.average:
      return [ValueTypes.ratio];
    default:
      return [ValueTypes.absolute, ValueTypes.ratio];
  }
};

const makeAttributeMapItem = (
  enabledLineChartTimeGroupings: Array<LineChartTimeGroupingType> = defaultLineChartTimeGroupings,
  enabledDisplays = [
    WidgetDisplayOptions.barChart,
    WidgetDisplayOptions.lineChart,
    WidgetDisplayOptions.pieChart,
    WidgetDisplayOptions.table,
  ],
  enabledValueTypes = [ValueTypes.absolute, ValueTypes.ratio],
  defaultDisplay: WidgetDisplayOptions | null = WidgetDisplayOptions.barChart,
): AttributeMapItem => ({
  enabledLineChartTimeGroupings,
  enabledDisplays,
  enabledValueTypes,
  defaultDisplay,
});

const makeNumberAttributesMapItem = (attribute: AllAttributesType) => makeAttributeMapItem(
  [],
  [WidgetDisplayOptions.singleNumber, WidgetDisplayOptions.table],
  getEnabledValueTypes(attribute),
  WidgetDisplayOptions.singleNumber,
);

const makeDateAttributesMapItem = () => makeAttributeMapItem(
  [],
  [
    WidgetDisplayOptions.barChart,
    WidgetDisplayOptions.lineChart,
    WidgetDisplayOptions.pieChart,
    WidgetDisplayOptions.table,
  ],
  [ValueTypes.absolute, ValueTypes.ratio],
  WidgetDisplayOptions.lineChart,
);

const makeAttributesMap = (): Map<AllAttributesType, AttributeMapItem> => new Map(
  defaultAttrs().map(attribute => [
    attribute,
    [
      allAttrsEnum.day,
      allAttrsEnum.week,
      allAttrsEnum.month,
      allAttrsEnum.quarter,
      allAttrsEnum.year,
    ].includes(attribute) ? makeDateAttributesMapItem() : makeAttributeMapItem(),
  ]),
);

export const analyticsMap: AnalyticsMap = new Map([
  ['submitted-forms', {
    enabledValueTypes: [ValueTypes.absolute, ValueTypes.ratio],
    enabledAttributes: makeAttributesMap()
      .set('punctuality', makeAttributeMapItem())
      .set('total', makeNumberAttributesMapItem(allAttrsEnum.total))
      .set('approval-status', makeAttributeMapItem())
      .set('approvers', makeAttributeMapItem()),
  }],
  ['form-score', {
    enabledValueTypes: [ValueTypes.ratio],
    enabledAttributes: makeAttributesMap()
      .set('punctuality', makeAttributeMapItem())
      .set('average', makeNumberAttributesMapItem(allAttrsEnum.average))
      .set('form-item', makeAttributeMapItem())
      .set('page-section', makeAttributeMapItem())
      .set('approval-status', makeAttributeMapItem())
      .set('approvers', makeAttributeMapItem()),
  }],
  ['form-items', {
    enabledValueTypes: [ValueTypes.absolute, ValueTypes.ratio],
    enabledAttributes: makeAttributesMap()
      .set('punctuality', makeAttributeMapItem())
      .set('total', makeNumberAttributesMapItem(allAttrsEnum.total))
      .set('response', makeAttributeMapItem())
      .set('approval-status', makeAttributeMapItem())
      .set('approvers', makeAttributeMapItem()),
  }],
  ['negative-responses', {
    enabledValueTypes: [ValueTypes.absolute, ValueTypes.ratio],
    enabledAttributes: makeAttributesMap()
      .set('punctuality', makeAttributeMapItem())
      .set('total', makeNumberAttributesMapItem(allAttrsEnum.total))
      .set('page-section', makeAttributeMapItem())
      .set('form-item', makeAttributeMapItem())
      .set('response', makeAttributeMapItem())
      .set('approval-status', makeAttributeMapItem())
      .set('approvers', makeAttributeMapItem()),
  }],
  ['form-item-score', {
    enabledValueTypes: [ValueTypes.ratio],
    enabledAttributes: makeAttributesMap()
      .set('punctuality', makeAttributeMapItem())
      .set('average', makeNumberAttributesMapItem(allAttrsEnum.average))
      .set('approval-status', makeAttributeMapItem())
      .set('approvers', makeAttributeMapItem())
      .set('form-item', makeAttributeMapItem()),
  }],
  ['schedule-forms', {
    enabledValueTypes: [ValueTypes.absolute, ValueTypes.ratio],
    enabledAttributes: makeAttributesMap()
      .set('form-status', makeAttributeMapItem())
      .set('total', makeNumberAttributesMapItem(allAttrsEnum.total)),
  }],
  ['actions', {
    enabledValueTypes: [ValueTypes.absolute, ValueTypes.ratio],
    enabledAttributes: makeAttributesMap()
      .set('punctuality', makeAttributeMapItem())
      .set('total', makeNumberAttributesMapItem(allAttrsEnum.total))
      .set('action-status', makeAttributeMapItem())
      .set('creator', makeAttributeMapItem())
      .set('priority', makeAttributeMapItem())
      .set('form-item', makeAttributeMapItem())
      .set('page-section', makeAttributeMapItem())
      .set('approval-status', makeAttributeMapItem())
      .set('approvers', makeAttributeMapItem()),
  }],
]);
